// Static HTML generation for read-only answer bodies. Rendering every answer
// through a live ProseMirror EditorView is the dominant runtime CPU cost on
// low-power devices; read-only content is deterministic, so it can be
// serialized once with TipTap's schema and rendered as plain HTML.
import { getSchema } from '@tiptap/core'
import { DOMSerializer, Node as PmNode, type Schema } from '@tiptap/pm/model'
import StarterKit from '@tiptap/starter-kit'
import { KnovMention } from 'components/shared/tiptap/MentionExtension'
import { ImageExtension } from 'components/shared/tiptap/ImageExtension'
import { VideoExtension } from 'components/shared/tiptap/VideoExtension'
import {
    ActionPromptMarkExtension,
    ActionTokenExtension,
} from 'components/shared/tiptap/ActionTokenExtension'
import { applyMemeLinkMarks } from 'components/shared/tiptap/memeLinkMarks'
import { deltaToPmJson, type DeltaJson } from 'lib/value'
import { isBlockedExternalNavigation } from 'lib/externalNavigationPolicy'
import { isInternalSeoPath } from 'lib/seoPaths'
import {
    markBlockedExternalNavigationsInPmJson,
    SafeExternalLink,
} from 'components/shared/tiptap/blockedExternalNavigation'

type MentionStyles = Record<string, string | undefined>

const noop = () => {}

function isDeltaJson(value: unknown): value is DeltaJson {
    return Boolean(value && typeof value === 'object' && Array.isArray((value as DeltaJson).ops))
}

// Schema-bearing extensions mirroring TiptapEditor's editor config. Plugin-only
// extensions (mention suggestions, collapse, placeholder, the meme rewriter)
// contribute no schema and are intentionally absent — the meme rewrite is
// applied to the doc JSON by applyMemeLinkMarks instead.
export function buildStaticAnswerExtensions(mentionStyles: MentionStyles) {
    return [
        StarterKit.configure({
            hardBreak: {
                HTMLAttributes: {},
            },
            bulletList: {
                keepMarks: false,
                keepAttributes: false,
                HTMLAttributes: {
                    class: 'tiptap-bullet-list',
                },
            },
            orderedList: {
                keepMarks: false,
                keepAttributes: false,
                HTMLAttributes: {
                    class: 'tiptap-ordered-list',
                },
            },
            listItem: {
                HTMLAttributes: {
                    class: 'tiptap-list-item',
                },
            },
        }),
        SafeExternalLink.configure({
            // isInternalSeoPath first: meme links carry a real /meme/:text href
            // so crawlers see thread -> hub edges, and tiptap's default URI
            // validation (built for absolute URLs) would otherwise strip it.
            isAllowedUri: (url, context) =>
                isInternalSeoPath(url) ||
                isBlockedExternalNavigation(url) ||
                context.defaultValidate(url),
            HTMLAttributes: {
                target: '_blank',
                rel: 'noopener noreferrer',
            },
        }),
        KnovMention.configure({
            HTMLAttributes: {},
            mentionClass: mentionStyles?.mention,
            userMentionClass: mentionStyles?.userMention,
            modelMentionClass: mentionStyles?.modelMention,
            referenceMentionClass: mentionStyles?.referenceMention,
            // A configured handler makes renderHTML add the clickable styling
            // class; actual clicks are delegated by StaticAnswerBody.
            onMentionClick: noop,
        }),
        ImageExtension.configure({ HTMLAttributes: {} }),
        VideoExtension.configure({ HTMLAttributes: {} }),
        ActionPromptMarkExtension,
        ActionTokenExtension.configure({}),
    ]
}

// tiptap's generateHTML resolves the extensions into a fresh schema, serializer and
// scratch document on every call, which was most of each post's mount cost while a
// feed scrolls. The extensions only vary with the mention styles, so build those
// once per styles object and serialize the same way (getHTMLFromFragment).
type StaticAnswerRenderer = { schema: Schema; serializer: DOMSerializer; scratch: Document }
const renderersByStyles = new WeakMap<MentionStyles, StaticAnswerRenderer>()

function rendererFor(mentionStyles: MentionStyles): StaticAnswerRenderer {
    const key = mentionStyles || EMPTY_STYLES
    let renderer = renderersByStyles.get(key)
    if (!renderer) {
        const schema = getSchema(buildStaticAnswerExtensions(mentionStyles))
        renderer = {
            schema,
            serializer: DOMSerializer.fromSchema(schema),
            scratch: document.implementation.createHTMLDocument(),
        }
        renderersByStyles.set(key, renderer)
    }
    return renderer
}

const EMPTY_STYLES: MentionStyles = {}

function serializeToHtml(pmJson: unknown, mentionStyles: MentionStyles): string {
    const { schema, serializer, scratch } = rendererFor(mentionStyles)
    const contentNode = PmNode.fromJSON(schema, pmJson as Record<string, unknown>)
    const fragment = serializer.serializeFragment(contentNode.content)
    const container = scratch.createElement('div')
    container.appendChild(fragment)
    return container.innerHTML
}

// Returns the serialized HTML for a read-only answer body, or null when the
// content can't be statically rendered — callers must fall back to the live
// read-only editor in that case.
export function generateStaticAnswerHtml(
    deltaJson: unknown,
    mentionStyles: MentionStyles,
    { showMemeBrackets = true }: { showMemeBrackets?: boolean } = {},
): string | null {
    try {
        if (!deltaJson) return ''
        // CommonEditor supports raw string content in some composer paths;
        // answers always carry Delta JSON. Punt strings to the live editor.
        if (typeof deltaJson === 'string') return null
        if (!isDeltaJson(deltaJson)) return null

        const pmJson = deltaToPmJson(deltaJson)
        if (!pmJson || !Array.isArray(pmJson.content) || pmJson.content.length === 0) {
            return ''
        }

        const withMemes = applyMemeLinkMarks(pmJson, mentionStyles?.meme, {
            showBrackets: showMemeBrackets,
        })
        const withBlockedExternalNavigations = markBlockedExternalNavigationsInPmJson(withMemes)
        return serializeToHtml(withBlockedExternalNavigations, mentionStyles)
    } catch (error) {
        console.warn('Static answer render failed; falling back to live editor', error)
        return null
    }
}
